import { cloneDeep } from 'lodash';
import api from '../baseAPIs/core';
import { CustomilyTemplate, CustomilyTemplateDto } from '@extTypes/api/customily/template';
import { LibraryCategory } from '@extTypes/api/customily/libraries';
import type {
    LinkedPodProductsResponse,
    LinkedTemplateProductsByProvider,
    ManualTemplateProductList,
    PrintAreaCombinationDTO,
    ProviderTemplateProductDTO,
    ResultListDTO
} from './typings';
import { TemplateProductVariants } from '@extTypes/providers/manualPod/product';
import { BestMatchPodManualDTO, PodPlaceholders, TemplatePrintAreasDTO } from '@extTypes/pod/productInfo';

const templateRequestsInFlight = new Map<string, Promise<CustomilyTemplate>>();

// Many inputs can ask for the same template at once (e.g. one per variant row), which trips the API rate limit.
//  Concurrent calls share one request; nothing is kept once it settles, so later calls always read fresh data.
export const getProductTemplate = async (productId: string, useListEPS = false): Promise<CustomilyTemplate> => {
    const key = `${productId}|${useListEPS}`;

    let request = templateRequestsInFlight.get(key);
    if (!request) {
        request = api
            .get('Product', {
                params: {
                    productId,
                    useListEPS
                }
            })
            .then((response) => response.data as CustomilyTemplate)
            .finally(() => templateRequestsInFlight.delete(key));
        templateRequestsInFlight.set(key, request);
    }

    // Each caller gets its own copy: callers keep and edit the template
    return cloneDeep(await request);
};

export const getProductTemplates = async (productIds: string[], useListEPS = false): Promise<CustomilyTemplate[]> => {
    const response = await api.post('Product/GetProducts/ByIds', {
        productIds,
        useListEPS
    });

    return response.data;
};

export const getTemplates = async (
    searchString: string,
    from: number,
    count: number
): Promise<CustomilyTemplateDto[]> => {
    const response = await api.get('Product/GetProducts', {
        params: {
            searchString: searchString,
            from: from,
            pageSize: count
        }
    });

    return response.data;
};

export const getTemplatesCount = async (searchString: string): Promise<number> => {
    const response = await api.get('Product/GetProductsCount', {
        params: {
            searchString: searchString
        }
    });

    return response.data;
};

export const deleteTemplatesByIds = (ids: string[], forceDelete?: boolean): Promise<void> => {
    return api.post('Product/DeleteProducts/ByIds', {
        ids,
        forceDelete
    });
};

export const deleteTemplateById = (templateId: string, forceDelete?: boolean): Promise<void> => {
    return api.delete('Product/DeleteProductById', {
        params: {
            id: templateId,
            forceDelete
        }
    });
};

export const getProductLibraryName = async (id: number): Promise<string> => {
    const response = await api.get(`Libraries/${id}/Name`);

    return response.data;
};

export const getProductLibraryCategories = async (id: number): Promise<LibraryCategory[]> => {
    const response = await api.get(`Libraries/${id}/Categories`, {
        params: {
            searchString: '',
            from: 0,
            count: 0
        }
    });

    return response.data;
};

export const getLinkedPodProducts = async (
    podService: 'PRINTFUL' | 'PRINTIFY',
    podProductId?: string
): Promise<LinkedPodProductsResponse> => {
    const response = await api.get('TemplateProducts/LinkedPodProducts', {
        params: { podService, podProductId }
    });
    return response.data;
};

export const getLinkedTemplateByProducts = async <T extends keyof LinkedTemplateProductsByProvider>(
    podService: T,
    productId: string
): Promise<{
    podData: LinkedTemplateProductsByProvider[T];
    printProviderData: { printProvider: string; isMultiFile: boolean }[];
}> => {
    const response = await api.get('TemplateProducts/LinkedPodVariants', {
        params: { productId, podService }
    });

    return response.data;
};

export const getManualPodProducts = async (podId: number): Promise<ManualTemplateProductList> => {
    return api
        .get<ManualTemplateProductList>('TemplateProducts/ManualPodProducts', {
            params: {
                podId
            }
        })
        .then(({ data }) => data);
};

export const getTemplateProductById = async (templateId: string): Promise<ProviderTemplateProductDTO> => {
    return api
        .get<ProviderTemplateProductDTO>(`TemplateProducts/GetTemplateProduct/${templateId}`)
        .then(({ data }) => data);
};

export const getTemplateProductVariantsByProductId = (id: string): Promise<TemplateProductVariants> =>
    api.get(`TemplateProductVariants/${id}`).then(({ data }) => data);

export const listTemplateProducts = async (
    from: number,
    count: number,
    search?: string
): Promise<ResultListDTO<ProviderTemplateProductDTO>> =>
    api
        .get<ResultListDTO<ProviderTemplateProductDTO>>('/TemplateProducts/ListTemplateProducts', {
            params: { from, count, search }
        })
        .then(({ data }) => data);

export const getPrintAreasCombinations = async (
    podId: number,
    podProductId: string
): Promise<PrintAreaCombinationDTO[]> =>
    api
        .get<PrintAreaCombinationDTO[]>('/TemplateProducts/GetPrintAreaCombinations', {
            params: { podId, podProductId }
        })
        .then(({ data }) => data);

export const getBestMatchTemplateProductIds = async (podPlaceholders: PodPlaceholders): Promise<string[]> => {
    const response = await api.post('Product/BestMatchTemplateProductIds', podPlaceholders);
    return response.data;
};

export const getBestMatchPodManual = async (
    bestMatchPodManualDTO: BestMatchPodManualDTO
): Promise<TemplatePrintAreasDTO> => {
    const response = await api.post('TemplateProducts/BestMatchPodManual', bestMatchPodManualDTO);
    return response.data;
};
